import moment from 'moment'
import React, { useState } from 'react'
import styled, { css } from 'styled-components'
import { Text } from 'sutra/components/Text/Text'
import { gray } from 'sutra/tokens/colors'
import { spacing } from 'sutra/tokens/spacing'

import { handleException } from 'clients/exception-handling'
import { highlightPost } from 'components/post-overview/PostUtils'
import { SkeletonBlock } from 'components/post-overview/skeleton-block'
import { SourceAndIcons } from 'components/post-overview/SourceAndIcons'
import { Stars } from 'components/post-overview/Stars'
import {
  PROMOTION_ENABLED_HIGHLIGHT_COLOR,
  SEARCH_HIGHLIGHT_COLOR,
} from 'features/project/utils'
import { ALIAS_FOR_NULL_VALUES } from 'src/constants/filter-values'
import type { PostDetails } from 'types/project'

const ProductImg = styled.img<{ $stacked?: boolean }>`
  object-fit: contain;

  ${({ $stacked }) =>
    $stacked
      ? css`
          align-self: flex-start;
          max-width: 100%;
          max-height: 120px;
          padding: 0 0 ${spacing[3]}px;
        `
      : css`
          max-width: 50%;
          max-height: 200px;
          padding: ${spacing[5]}px;
        `}
`

const Layout = styled.div<{ $stacked?: boolean }>`
  display: flex;
  flex-direction: ${({ $stacked }) => ($stacked ? 'column' : 'row')};
`

const Body = styled.div`
  flex: 1;
`

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${spacing[1]}px;
`

// highlightPost (see PostUtils.tsx) returns either spans (a
// <Yogi-highlight>-tagged body) or a bare string, and neither sets a
// font-size of its own. Every ancestor up through PopoverModal's
// Panel/Content is likewise silent on font-size, so without this explicit
// rule the text falls all the way back to the browser default (16px).
const ReviewText = styled.div`
  margin-top: ${spacing[2]}px;
  overflow-wrap: anywhere;
  font-size: 14px;
  letter-spacing: -0.02em;
`

export const PostOverviewSection: React.FC<{
  post?: PostDetails
  stacked?: boolean
  trailingAction?: React.ReactNode
  // Caller-supplied — see SourceAndIcons; this shared component doesn't
  // read feature flags itself.
  showUsername?: boolean
  hideImage?: boolean
}> = (props) => {
  const [isPromotionHovered, setPromotionHoverState] = useState(false)
  const [isViewableImage, setViewableImage] = useState(true)

  if (!props.post) {
    return (
      <Layout $stacked={props.stacked}>
        <Body>
          <HeaderRow>
            <SkeletonBlock $width="100px" />
          </HeaderRow>
          <SkeletonBlock $width="60%" $height="72px" />
          <HeaderRow style={{ marginTop: spacing[2] }}>
            <SkeletonBlock $width="150px" />
          </HeaderRow>
        </Body>
      </Layout>
    )
  }

  return (
    <Layout $stacked={props.stacked}>
      {!props.hideImage && isViewableImage && props.post.product_image_url && (
        <ProductImg
          $stacked={props.stacked}
          src={props.post.product_image_url}
          onError={(error) => {
            setViewableImage(false)
            handleException(error, {
              operation: 'post.image.load',
              disposition: 'recovered',
              reason:
                'Post details remain usable without the optional product image.',
            })
          }}
        />
      )}
      <Body>
        <HeaderRow>
          {props.post.score &&
            String(props.post.score) !== ALIAS_FOR_NULL_VALUES && (
              <Stars score={props.post.score} style={{ flex: 1 }} />
            )}
          <Text $size="sm" style={{ color: gray[500] }}>
            {moment.unix(props.post.create_time).utc().format('MM/DD/yyyy')}
          </Text>
        </HeaderRow>
        {props.post.title && (
          <Text $size="sm" style={{ fontWeight: 600, color: gray[800] }}>
            {props.post.title}
          </Text>
        )}
        <ReviewText id="review-content">
          {props.post.is_promoted
            ? highlightPost({
                postBody: props.post.body,
                color: PROMOTION_ENABLED_HIGHLIGHT_COLOR,
                showHighlights: isPromotionHovered,
              })
            : highlightPost({
                postBody: props.post.body,
                color: SEARCH_HIGHLIGHT_COLOR,
                showHighlights: true,
              })}
        </ReviewText>
        <SourceAndIcons
          post={props.post}
          onPromoHover={setPromotionHoverState}
          trailingAction={props.trailingAction}
          showUsername={props.showUsername}
        />
      </Body>
    </Layout>
  )
}
